import React, { useEffect, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Grid, Paper, Container, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import Can from 'components/Can';
import { buildUrl } from 'utils';
import Logs from 'components/Logs';
import { useConfirm } from 'hooks';
import Title from 'components/Title';
import Layout from 'components/Layout';
import useStyles from './Details.styled';
import Comments from 'components/Comments';
import { useParams } from 'react-router-dom';
import { parseBusinessHoursData } from 'services/rules';
import { BusinessHoursActions } from 'state/modules/businessHours';
import { BusinessHoursTable, AssociatedVehileGroups } from 'components/BusinessHours';

const BusinessHoursDetails = ({ history }) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const { id } = useParams();
  const {
    current: { name, bh_rules, description, error_message, ...extra },
  } = useSelector((state) => state.businessHours);

  const [toggleArchiveBusinessHours, ArchiveBusinessHoursConfirm] = useConfirm({
    title: i18n.t(`business_hours.${extra?.enabled ? 'archive_title' : 'activate_title'}`),
    message: i18n.t(`business_hours.${extra?.enabled ? 'archive_confirm' : 'activate_confirm'}`),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t(i18n.t(`business_hours.${extra?.enabled ? 'archive' : 'activate'}`)),
    onConfirm: useCallback(() => {
      dispatch(BusinessHoursActions.fetchUpdateBusinessHours(id, { archived: extra.enabled }));
    }, [id, dispatch, extra]),
  });

  useEffect(() => {
    if (id) {
      dispatch(BusinessHoursActions.fetchBusinessHourById(id));
    }

    return () => {
      dispatch(BusinessHoursActions.resetCurrentBusinessHour());
    };
  }, [id, dispatch]);

  return (
    <Layout title={i18n.t('business_hours.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={4} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Title title={name || ''} />
              </Grid>
              <Grid item>
                <Grid container spacing={4} direction="row" alignItems="center" justifyContent="flex-end">
                  <Can I="update" a="business_hours_rules_set">
                    <Grid item>
                      <Button
                        onClick={() => {
                          history.push(buildUrl(`/business-hours/${id}/update`));
                        }}
                        style={{ textTransform: 'capitalize' }}
                        variant="outlined"
                        color="primary"
                      >
                        {i18n.t('common.update')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        onClick={() => {
                          history.push(buildUrl(`/business-hours/${id}/clone`));
                        }}
                        style={{ textTransform: 'capitalize' }}
                        variant="outlined"
                        color="primary"
                      >
                        {i18n.t('business_hours.duplicate')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        style={{ textTransform: 'capitalize' }}
                        onClick={toggleArchiveBusinessHours}
                        variant="outlined"
                        color="primary"
                      >
                        {i18n.t(`business_hours.${extra?.enabled ? 'archive' : 'activate'}`)}
                      </Button>
                    </Grid>
                  </Can>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={4} direction="row">
              <Grid item xs={12}>
                <Paper elevation={2} className={classes.paper}>
                  {description && (
                    <>
                      <Typography variant="h6" align="left">
                        <Box color="text.subtitle" fontWeight="bold" fontSize={14}>
                          {i18n.t('common.description')}
                        </Box>
                      </Typography>
                      <Typography variant="h6" align="left" style={{ marginTop: 5 }}>
                        <Box color="text.subtitle" fontWeight={400} fontSize={13}>
                          {description}
                        </Box>
                      </Typography>
                    </>
                  )}
                  <Grid container>
                    <Grid item xs={12} style={{ marginTop: 15 }}>
                      <BusinessHoursTable
                        id={id}
                        action="details"
                        bh={parseBusinessHoursData(bh_rules)}
                      />
                    </Grid>
                    {error_message && (
                      <>
                        <Grid item xs={12} style={{ marginTop: 30 }}>
                          <Typography variant="h6" align="left">
                            <Box color="text.subtitle" fontWeight="bold" fontSize={14}>
                              {i18n.t('business_hours.error_msg_title')}
                            </Box>
                          </Typography>
                        </Grid>
                        <Grid item xs={12} style={{ marginTop: 5 }}>
                          <Typography variant="h6" align="left">
                            <Box color="text.subtitle" fontWeight={400} fontSize={13}>
                              {error_message}
                            </Box>
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={4} direction="row">
                <Grid item xs={12} sm={6} md={4}>
                  <AssociatedVehileGroups id={parseInt(id)} />
                </Grid>
                <Can I="read" a="comment">
                  <Grid item xs={12} sm={6} md={4}>
                    <Comments type="business_hours_rules_set" id={parseInt(id)} />
                  </Grid>
                </Can>
                <Can I="read" on="event_log">
                  <Grid item xs={12} sm={6} md={4}>
                    <Logs
                      targetId={parseInt(id)}
                      targetType="BusinessHoursRulesSet"
                      title={i18n.t('tax_ruleset.logs')}
                    />
                  </Grid>
                </Can>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <ArchiveBusinessHoursConfirm />
    </Layout>
  );
};

export default withRouter(BusinessHoursDetails);
